import React, { useMemo } from "react";
import {
    SummaryCard,
    SummaryHeader,
    SummaryTitle,
    SummarySubtitle,
    SummaryModulesList,
    SummaryModuleItem,
    SummaryModuleInfo,
    SummaryModuleName,
    SummaryModuleDetails,
    SummaryModulePrice,
    SummaryFooter,
    ModuleSummaryContainer,
    ModuleSummaryHeader,
    ModuleSummaryTopRow,
    ModuleSummaryTitleGroup,
    ModuleSummaryTitle,
    ModuleSummaryMeta,
    ModuleSummaryAmount,
    ProgressTrackWrapper,
    ProgressTrackBackground,
    ProgressTrackFill,
    ProgressTrackLabel,
    ProgressTrackHelper,
    SummarySection,
    SummaryRow,
    SummaryRowTitle,
    SummaryRowAmount,
    SummaryHelperText,
    SummaryTotalRow,
    SummaryTotalLabel,
    SummaryTotalAmount,
    SummaryDisclaimer,
    SummaryDisclaimerText,
    TotalAsterisk,
    MobileSummaryFooter
} from "../styles/summaryStyles";
import { ButtonSecondary, ButtonDoneEditing, ActionButton, ActionButtonSpacer } from "../styles/primitives";
import ModuleTypeTag from "../modules/ModuleTypeTag";
import { normalizeModuleId } from "../../shared/utils/moduleHelpers";

const formatCurrency = (amount) => {
    const numericAmount = Number(amount) || 0;
    return `R ${numericAmount.toLocaleString("en-ZA", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    })}`;
};

const ModuleSummaryCard = ({ 
    selectedRedoModules, 
    selectedProgressionModules, 
    selectedCreditModules,
    allModules,
    minModules,
    maxModules,
    onEditSelection,
    onDoneEditing,
    onRemoveModule,
    isEditMode,
    readOnly,
    // Module summary props (from sidebar)
    items = [],
    modulesCountSelected = 0,
    modulesRequiredMin = 0,
    modulesAllowedMax = 0,
    modulesTotal = 0,
    adjustmentsTotal = 0,
    depositAmount: apiDepositAmount = 0,
    totalAmount: apiTotalAmount = 0,
    deliveryMethodLabel,
    submitted = false
}) => {
    const getModuleById = (id) => {
        const normalizedId = normalizeModuleId(id);
        return allModules.find(m => normalizeModuleId(m.netsuite_product_id) === normalizedId);
    };
    
    // Helper to check if a module is inactive or not eligible
    const isInactiveOrNotEligible = (module) => {
        if (!module) return false;
        const moduleStatus = (module.module_status || "").toLowerCase().trim();
        const status = (module.status || "").toLowerCase().trim();
        const reason = (module.reason || "").toLowerCase().trim();
        
        const inactivePatterns = ["inactive", "upcoming"];
        const notEligiblePatterns = ["not eligible", "noteligible", "not_eligible", "deregister"];
        
        const checkPatterns = (text, patterns) => patterns.some(p => text.includes(p));
        
        return (
            checkPatterns(moduleStatus, inactivePatterns) ||
            checkPatterns(status, [...inactivePatterns, ...notEligiblePatterns]) ||
            checkPatterns(reason, [...inactivePatterns, ...notEligiblePatterns])
        );
    };

    // Create selected items, prioritizing redo over progression to avoid duplicates
    // Filter out inactive/not-eligible modules for redo and progression types (credit modules are always shown)
    const seenIds = new Set();
    const selectedItems = [
        ...selectedRedoModules.map(id => ({ id, type: "redo", module: getModuleById(id) })),
        ...selectedProgressionModules.map(id => ({ id, type: "progression", module: getModuleById(id) })),
        ...selectedCreditModules.map(id => ({ id, type: "credit", module: getModuleById(id) }))
    ].filter(item => {
        if (!item.module) return false;
        const moduleId = normalizeModuleId(item.module.netsuite_product_id);
        if (seenIds.has(moduleId)) return false;
        seenIds.add(moduleId);
        
        // For redo and progression types, exclude inactive/not-eligible modules
        // Credit/adjustment modules are always shown (they represent deductions)
        if (item.type === "redo" || item.type === "progression") {
            if (isInactiveOrNotEligible(item.module)) {
                return false;
            }
        }
        
        return true;
    });

    const totalModules = selectedItems.filter(
        (item) => item.type === "redo" || item.type === "progression"
    ).length;
    const hasMin = minModules > 0;
    const hasMax = maxModules > 0;

    const alreadyEnrolledCount = useMemo(() => {
        const source = Array.isArray(allModules) ? allModules : Object.values(allModules || {});
        return source.filter((m) => {
            const status = String(m?.status || "").trim().toLowerCase();
            const reason = String(m?.reason || "").trim().toLowerCase();
            return status === "enrolled" || reason === "enrolled";
        }).length;
    }, [allModules]);

    // Calculate totals for mobile footer (same logic as sidebar)
    const calculatedTotal = useMemo(() => {
        if (submitted && apiTotalAmount > 0) {
            return Number(apiTotalAmount);
        }
        return Math.max(0, (Number(modulesTotal) || 0) - (Number(adjustmentsTotal) || 0));
    }, [modulesTotal, adjustmentsTotal, submitted, apiTotalAmount]);
    
    const calculatedDeposit = useMemo(() => {
        if (submitted && apiDepositAmount > 0) {
            return Number(apiDepositAmount);
        }
        const finalTotal = calculatedTotal;
        if (finalTotal <= 0) {
            return 0;
        }
        if (finalTotal >= 14000) {
            return 7000;
        }
        return Number((finalTotal * 0.5).toFixed(2));
    }, [calculatedTotal, submitted, apiDepositAmount]);

    // Progress calculation (same as sidebar)
    const progressPercent = useMemo(() => {
        const denominator = modulesRequiredMin > 0
            ? modulesRequiredMin
            : modulesAllowedMax > 0
                ? modulesAllowedMax
                : 0;

        if (!denominator || denominator <= 0) {
            return 0;
        }

        const ratio = modulesCountSelected / denominator;
        const clamped = Math.max(0, Math.min(1, ratio));
        const logicalPercent = clamped * 100;

        const minPercent = modulesCountSelected === 0 ? 30 : 35;
        return Math.max(minPercent, logicalPercent);
    }, [modulesCountSelected, modulesRequiredMin, modulesAllowedMax]);

    const isAboveMax = modulesAllowedMax > 0 && modulesCountSelected > modulesAllowedMax;

    const maxDisplayModules = modulesAllowedMax > 0
        ? modulesAllowedMax
        : modulesRequiredMin > 0
            ? modulesRequiredMin
            : 0;

    const progressStatus = modulesCountSelected === 0
        ? "none"
        : isAboveMax
            ? "error"
            : "ok";

    // Get student number from localStorage
    const userData = useMemo(() => {
        try {
            return JSON.parse(localStorage.getItem('user')) || {};
        } catch {
            return {};
        }
    }, []);
    const studentNumber = userData?.student_crm_id || "";

    return (
        <SummaryCard>
            <SummaryHeader>
                <SummaryTitle>Module Summary</SummaryTitle>
                {!readOnly && (
                    <SummarySubtitle>
                        {hasMin || hasMax ? (
                            <>
                                {(!hasMin && hasMax) && (
                                    <>You may select up to <span style={{ color: "var(--link-color)", fontWeight: 600 }}>{maxModules}</span> modules next semester.</>
                                )}
                                {(hasMin && !hasMax) && (
                                    <>You must be enrolled for at least <span style={{ color: "var(--link-color)", fontWeight: 600 }}>{minModules}</span> modules next semester.</>
                                )}
                                {(hasMin && hasMax && minModules === maxModules) && (
                                    <>You must be enrolled for exactly <span style={{ color: "var(--link-color)", fontWeight: 600 }}>{minModules}</span> modules next semester.</>
                                )}
                                {(hasMin && hasMax && minModules !== maxModules) && (
                                    <>You must be enrolled for between <span style={{ color: "var(--link-color)", fontWeight: 600 }}>{minModules}</span> and <span style={{ color: "var(--link-color)", fontWeight: 600 }}>{maxModules}</span> modules next semester.</>
                                )}
                                
                                <>
                                &nbsp;Currently selected: <span style={{ fontWeight: 600, color: "var(--link-color)" }}>{totalModules} module(s)</span> 
                                {alreadyEnrolledCount > 0 ? (
                                    <>
                                        &nbsp;in addition to <span style={{ fontWeight: 600, color: "var(--link-color)" }}>
                                            {alreadyEnrolledCount} already enrolled</span> module(s).
                                    </>
                                ) : (
                                    <>.</>
                                )}
                                </>
                              
                                
                            </>
                        ) : (
                            <>Select modules for next semester.</>
                        )}
                    </SummarySubtitle>
                )}
                <div style={{margin: "10px 0"}}></div>
                      {/* Mobile-only module summary section */}
            {items.length > 0 && (
                <ModuleSummaryContainer>
                    <ModuleSummaryHeader>
                        <ModuleSummaryTopRow>
                            <ModuleSummaryTitleGroup>
                                <ModuleSummaryTitle>Modules</ModuleSummaryTitle>
                                <ModuleSummaryMeta>
                                    <i className="fal fa-book" style={{ color: "var(--link-color)" }}></i>
                                    <span>{deliveryMethodLabel || "Programme modules"}</span>
                                </ModuleSummaryMeta>
                            </ModuleSummaryTitleGroup>
                            <ModuleSummaryAmount>{formatCurrency(modulesTotal)}</ModuleSummaryAmount>
                        </ModuleSummaryTopRow>
                        <ProgressTrackWrapper>
                            <ProgressTrackBackground>
                                <ProgressTrackFill
                                    $percent={progressPercent}
                                    $status={progressStatus}
                                >
                                    <ProgressTrackLabel>
                                        {`${modulesCountSelected} of ${maxDisplayModules} Modules`}
                                    </ProgressTrackLabel>
                                </ProgressTrackFill>
                            </ProgressTrackBackground>
                            <ProgressTrackHelper>
                                {modulesRequiredMin > 0 || modulesAllowedMax > 0 ? (
                                    modulesRequiredMin > 0 && modulesAllowedMax > 0 ? (
                                        modulesRequiredMin === modulesAllowedMax ? (
                                            `Exactly ${modulesRequiredMin} modules required`
                                        ) : (
                                            `Min ${modulesRequiredMin}, max ${modulesAllowedMax} modules`
                                        )
                                    ) : modulesRequiredMin > 0 ? (
                                        `Minimum ${modulesRequiredMin} modules`
                                    ) : (
                                        `Maximum ${modulesAllowedMax} modules`
                                    )
                                ) : (
                                    ""
                                )}
                            </ProgressTrackHelper>
                        </ProgressTrackWrapper>
                    </ModuleSummaryHeader>
       
                </ModuleSummaryContainer>
            )}
            </SummaryHeader>
            
            <SummaryModulesList>
                {selectedItems.map((item, idx) => {
                    const statusRaw = (item.module?.status || item.module?.reason || "").toLowerCase();
                    const isNotEligible = statusRaw === "deregister";

                    return (
                        <SummaryModuleItem key={idx} $isEdit={isEditMode}>
                            <SummaryModuleInfo>
                                <SummaryModuleName>{item.module?.name}</SummaryModuleName>
                                <SummaryModuleDetails>
                                    <SummaryModulePrice $isCredit={item.type === "credit"}>
                                        {new Intl.NumberFormat("en-ZA", {
                                            style: "currency",
                                            currency: "ZAR",
                                            minimumFractionDigits: 2,
                                            maximumFractionDigits: 2
                                        }).format(
                                            (item.type === "credit" ? -1 : 1) * (Number(item.module?.price) || 0)
                                        )}
                                    </SummaryModulePrice>
                                    <ModuleTypeTag type={item.type} />
                                </SummaryModuleDetails>
                            </SummaryModuleInfo>
                            {isEditMode && onRemoveModule && (
                                isNotEligible ? (
                                    <ActionButtonSpacer />
                                ) : (
                                    <ActionButton
                                        $variant="delete"
                                        onClick={() => onRemoveModule(item.module, item.type)}
                                        type="button"
                                        title="Remove from selection"
                                    >
                                       <i className="fa-light fa-trash-alt"></i>
                                    </ActionButton>
                                )
                            )}
                        </SummaryModuleItem>
                    );
                })}
                {selectedItems.length === 0 && !readOnly && (
                    <SummaryModuleItem style={{ color: "var(--lightGray3)", fontStyle: "italic" }}>
                        No modules selected
                    </SummaryModuleItem>
                )}
            </SummaryModulesList>
            {!readOnly && (
                <SummaryFooter>
                    {isEditMode ? (
                        <ButtonDoneEditing
                            onClick={onDoneEditing}
                            type="button"
                        >
                            <i className="fa-light fa-circle-check"></i>
                            <span>Done Editing</span>
                        </ButtonDoneEditing>
                    ) : (
                        <ButtonSecondary
                            onClick={onEditSelection}
                            type="button"
                        >
                            <i className="fa-light fa-edit"></i>
                            <span style={{ fontWeight: 600 }}>Edit Selection</span>
                        </ButtonSecondary>
                    )}
                </SummaryFooter>
            )}
      
            {/* Mobile-only footer with deposit, adjustments, and total */}
            {(items.length > 0 || modulesTotal > 0 || calculatedTotal > 0) && (
                <MobileSummaryFooter>
                    <SummarySection>
                        {adjustmentsTotal !== 0 && (
                            <div>
                                <SummaryRow>
                                    <SummaryRowTitle>Academic Adjustments</SummaryRowTitle>
                                    <SummaryRowAmount $color="#2ab554">
                                        -{formatCurrency(adjustmentsTotal)}
                                    </SummaryRowAmount>
                                </SummaryRow>
                                <SummaryHelperText>
                                    Any fees paid for these modules will be credited to your account
                                    based on the modules you choose to re-register for.
                                </SummaryHelperText>
                            </div>
                        )}
                        <div>
                            <SummaryRow>
                                <SummaryRowTitle>Deposit</SummaryRowTitle>
                                <SummaryRowAmount>{formatCurrency(calculatedDeposit)}</SummaryRowAmount>
                            </SummaryRow>
                            <SummaryHelperText $color="#c1232c">
                                The deposit must be paid to secure your spot for the upcoming
                                intake. Your student number{" "}
                                <strong>{studentNumber || "Your Student Number"}</strong> must be
                                used as the payment reference.
                            </SummaryHelperText>
                        </div>
                    </SummarySection>
                    <SummaryTotalRow>
                        <SummaryTotalLabel>Total</SummaryTotalLabel>
                        <SummaryTotalAmount>{formatCurrency(apiTotalAmount > 0 && submitted ? apiTotalAmount : calculatedTotal)}
                            <TotalAsterisk>
                                <i className="fa-solid fa-asterisk" />
                            </TotalAsterisk>
                        </SummaryTotalAmount>
                    </SummaryTotalRow>
                    <SummaryDisclaimer>
                        <i className="fas fa-exclamation-circle" />
                        <SummaryDisclaimerText>
                            Prices shown are an estimate and may not indicate the final price.
                        </SummaryDisclaimerText>
                    </SummaryDisclaimer>
                </MobileSummaryFooter>
            )}
        </SummaryCard>
    );
};

export default ModuleSummaryCard;

